import { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MissionWorkspace from "../../../components/EzeeAI/MissionWorkspace";
import { AuthContext } from "../../../helpers/AuthContext";

const STARTERS = [
  "What should I be paying attention to right now?",
  "Which projects have activity I should review?",
  "What purchase orders need follow-up?",
  "What invoices are still open?",
];

function usageLabel(usage) {
  if (!usage?.weekly) return null;
  return `${usage.weekly.used} / ${usage.weekly.limit} questions this week`;
}

function greetingForHour(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function dailyPromptForHour(hour) {
  if (hour < 12) return "Here’s what deserves your attention today.";
  if (hour < 18) return "Here’s a clear view of what matters right now.";
  return "Here’s a quick look at what matters before you wrap up.";
}

const BrainTab = ({ instanceUniqueId }) => {
  const { authState } = useContext(AuthContext);
  const [activeView, setActiveView] = useState("chat");
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [feedbackSubmittingIndex, setFeedbackSubmittingIndex] = useState(null);
  const [feedbackRecorded, setFeedbackRecorded] = useState({});
  const bottomRef = useRef(null);

  const now = new Date();
  const greeting = greetingForHour(now.getHours());
  const dayPrompt = dailyPromptForHour(now.getHours());
  const firstName = authState?.first_name || "there";
  const dateLabel = now.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const authHeaders = () => ({ accessToken: localStorage.getItem("accessToken") });

  const loadConversation = async (id) => {
    if (!id) return;
    const response = await fetch(
      `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain/conversations/${id}`,
      { headers: authHeaders() }
    );
    const payload = await response.json();
    if (!response.ok || !payload?.success) {
      throw new Error(payload?.message || "Unable to load Ezee AI conversation.");
    }
    setConversationId(Number(payload.conversation?.id || id));
    setFeedbackRecorded({});
    setMessages(
      (payload.messages || []).map((message) => ({
        role: message.role,
        content: message.content,
        sources: message.sources || [],
      }))
    );
  };

  const loadConversations = async (resumeLatest = false) => {
    const response = await fetch(
      `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain/conversations?limit=30`,
      { headers: authHeaders() }
    );
    const payload = await response.json();
    if (!response.ok || !payload?.success) return;
    const rows = payload.conversations || [];
    setConversations(rows);
    if (resumeLatest && !conversationId && rows[0]?.id) {
      await loadConversation(rows[0].id);
    }
  };

  useEffect(() => {
    let active = true;
    setAvailability(null);
    fetch(
      `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain/availability`,
      { headers: authHeaders() }
    )
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok || !payload?.success) throw new Error(payload?.message || "Unable to check Ezee availability.");
        if (active) setAvailability(payload.enabled === true);
      })
      .catch(() => { if (active) setAvailability("error"); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (availability !== true) return;
    let active = true;
    fetch(
      `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain/usage`,
      { headers: authHeaders() }
    )
      .then((response) => response.json())
      .then((payload) => {
        if (active && payload?.success) setUsage(payload.usage);
      })
      .catch(() => {});

    loadConversations(true).catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId, availability]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, loading]);

  const ask = async (value = question) => {
    const trimmed = String(value || "").trim();
    if (!trimmed || loading || availability !== true) return;

    setQuestion("");
    setError(null);
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accessToken: localStorage.getItem("accessToken"),
          },
          body: JSON.stringify({
            question: trimmed,
            conversationId,
            pageContext: { surface: "ai-brain" },
          }),
        }
      );
      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || "Ezee AI could not answer that question.");
      }
      setUsage(payload.usage || usage);
      if (payload.conversation?.id) setConversationId(Number(payload.conversation.id));
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: payload.answer,
          sources: payload.sources || [],
        },
      ]);
      loadConversations(false).catch(() => {});
    } catch (err) {
      setError(err.message || "Ezee AI could not answer that question.");
    } finally {
      setLoading(false);
    }
  };

  const reportIssue = async (message, index) => {
    if (feedbackSubmittingIndex !== null) return;
    const reason = window.prompt(
      "What was wrong or what should Ezee have done better?",
      ""
    );
    if (!String(reason || "").trim()) return;
    const previousUser = [...messages.slice(0, index)]
      .reverse()
      .find((item) => item.role === "user");

    setFeedbackSubmittingIndex(index);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain/feedback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accessToken: localStorage.getItem("accessToken"),
          },
          body: JSON.stringify({
            conversationId,
            question: previousUser?.content || "",
            answer: message.content || "",
            reason: String(reason).trim(),
            pageContext: { surface: "ai-brain" },
          }),
        }
      );
      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || "Unable to record Ezee feedback.");
      }
      setFeedbackRecorded((current) => ({ ...current, [index]: true }));
    } catch (err) {
      setError(err.message || "Unable to record Ezee feedback.");
    } finally {
      setFeedbackSubmittingIndex(null);
    }
  };

  return (
    <Stack spacing={2} className="brain-shell">
      <section className="brain-daily-hero" aria-label="Ezee daily briefing">
        <div className="brain-daily-hero-overlay" />
        <div className="brain-daily-hero-content">
          <Typography className="brain-daily-date">{dateLabel}</Typography>
          <Typography component="h1" className="brain-daily-title">
            {greeting}, <span>{firstName}.</span>
          </Typography>
          <Typography className="brain-daily-subtitle">{dayPrompt}</Typography>
          <Typography className="brain-daily-copy">
            Ask Ezee about customers, projects, sales orders, purchase orders, invoices, and the work that needs your attention.
          </Typography>
        </div>
        <div className="brain-daily-mark">
          <AutoAwesomeIcon fontSize="small" />
          <span>Ask Ezee</span>
        </div>
      </section>

      <Stack direction="row" spacing={1} className="brain-view-tabs">
        <Button
          size="small"
          variant={activeView === "chat" ? "contained" : "text"}
          color={activeView === "chat" ? "primary" : "inherit"}
          onClick={() => setActiveView("chat")}
        >
          Chat
        </Button>
        <Button
          size="small"
          variant={activeView === "missions" ? "contained" : "text"}
          color={activeView === "missions" ? "primary" : "inherit"}
          onClick={() => setActiveView("missions")}
        >
          Missions
        </Button>
      </Stack>

      {availability === false ? (
        <Paper elevation={0} sx={{ px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 }, border: "1px solid #dfe8f4", borderRadius: 3, textAlign: "center", background: "radial-gradient(ellipse at bottom, #edf4ff 0%, #fff 65%)" }}>
          <Box sx={{ width: 72, height: 72, borderRadius: 3, mx: "auto", mb: 2, display: "grid", placeItems: "center", background: "linear-gradient(145deg, #f5f9ff, #cfe1ff)", color: "#2c6de1", boxShadow: "0 8px 24px rgba(48,101,190,.12)" }}>
            <AutoAwesomeIcon sx={{ fontSize: 37 }} />
          </Box>
          <Typography variant="h5" fontWeight={800} sx={{ mb: 1, color: "#14253f" }}>Ezee AI is coming soon</Typography>
          <Typography color="textSecondary" sx={{ maxWidth: 660, mx: "auto" }}>
            Your company doesn’t have Ezee AI enabled yet. Once activated, you’ll be able to ask questions, review live Elite Core data, and jump directly to the records used.
          </Typography>
          <Chip icon={<InfoOutlinedIcon />} label="Not enabled for your company" sx={{ mt: 2, bgcolor: "#e8f1ff", color: "#244d89", fontWeight: 600 }} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 4 }}>
            <TextField fullWidth disabled placeholder="Ask Ezee AI what’s happening..." />
            <Button variant="contained" disabled sx={{ minWidth: 90 }}>Ask</Button>
          </Stack>
          <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 1.5, textAlign: "left" }}>
            Ezee AI will become available here once this feature is enabled.
          </Typography>
        </Paper>
      ) : availability === null ? (
        <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress size={24} aria-label="Checking Ezee availability" /></Box>
      ) : availability === "error" ? (
        <Alert severity="info">Ezee AI availability could not be checked. Please refresh the page.</Alert>
      ) : activeView === "missions" ? (
        <MissionWorkspace
          instanceUniqueId={instanceUniqueId}
          onStartChat={(prompt) => {
            setActiveView("chat");
            if (prompt) setQuestion(prompt);
          }}
        />
      ) : (
        <>
          <Paper className="brain-intro-card" elevation={0}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                  <div className="brain-orb">
                    <AutoAwesomeIcon fontSize="small" />
                  </div>
                  <Typography variant="h5" fontWeight={800}>
                    Ezee AI
                  </Typography>
                  <Chip
                    label="Role scoped"
                    size="small"
                    icon={<LockOutlinedIcon />}
                    variant="outlined"
                  />
                </Stack>
                <Typography color="textSecondary" className="brain-intro-copy">
                  Ezee checks live Elite Core data and points you back to the records it used. Controlled actions are only available when your role permits them.
                </Typography>
              </Box>
              {usageLabel(usage) && (
                <Chip label={usageLabel(usage)} size="small" className="brain-usage-chip" />
              )}
            </Stack>
          </Paper>

          <Paper className="brain-conversation-bar" elevation={0}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ sm: "center" }}
            >
              <TextField
                select
                size="small"
                fullWidth
                label="Conversation"
                value={conversationId || ""}
                onChange={(event) => {
                  const nextId = Number(event.target.value);
                  if (nextId) {
                    loadConversation(nextId).catch((err) => setError(err.message));
                  } else {
                    setConversationId(null);
                    setMessages([]);
                    setFeedbackRecorded({});
                  }
                }}
              >
                <MenuItem value="">
                  <em>New conversation</em>
                </MenuItem>
                {conversations.map((conversation) => (
                  <MenuItem key={conversation.id} value={conversation.id}>
                    {conversation.title || `Conversation #${conversation.id}`}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                startIcon={<AddCommentOutlinedIcon />}
                onClick={() => {
                  setConversationId(null);
                  setMessages([]);
                  setFeedbackRecorded({});
                  setQuestion("");
                  setError(null);
                }}
                sx={{ whiteSpace: "nowrap" }}
              >
                New chat
              </Button>
            </Stack>
          </Paper>

          {!messages.length && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="h6" fontWeight={800}>
                  Try asking
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Personalized by your access
                </Typography>
              </Stack>
              <div className="brain-starters">
                {STARTERS.map((starter) => (
                  <button key={starter} className="brain-starter" onClick={() => ask(starter)}>
                    <span>{starter}</span>
                    <ArrowForwardIcon fontSize="small" />
                  </button>
                ))}
              </div>
            </Box>
          )}

          {!!messages.length && (
            <Paper className="brain-thread" elevation={0}>
              <Stack spacing={1.5}>
                {messages.map((message, index) => (
                  <Box
                    key={`${message.role}-${index}`}
                    className={`brain-message brain-message-${message.role}`}
                  >
                    <Typography variant="caption" fontWeight={800} className="brain-message-label">
                      {message.role === "user" ? "You" : "Ezee AI"}
                    </Typography>
                    <Typography variant="body2" className="brain-message-copy">
                      {message.content}
                    </Typography>
                    {message.role === "assistant" ? (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<ReportProblemOutlinedIcon sx={{ fontSize: 16 }} />}
                        disabled={feedbackSubmittingIndex === index || feedbackRecorded[index]}
                        onClick={() => reportIssue(message, index)}
                        sx={{
                          mt: 0.75,
                          px: 0,
                          minWidth: 0,
                          color: "text.secondary",
                          textTransform: "none",
                        }}
                      >
                        {feedbackRecorded[index] ? "Issue recorded" : "Report issue"}
                      </Button>
                    ) : null}
                    {!!message.sources?.length && (
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" mt={1.25}>
                        {message.sources.map((source) => (
                          <Chip
                            key={`${source.type}-${source.id}-${source.url}`}
                            label={source.label}
                            size="small"
                            component="a"
                            clickable
                            href={source.url}
                            className="brain-source-chip"
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                ))}
                {loading && (
                  <Stack direction="row" spacing={1} alignItems="center" className="brain-thinking">
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="textSecondary">
                      Checking Elite Core...
                    </Typography>
                  </Stack>
                )}
                <div ref={bottomRef} />
              </Stack>
            </Paper>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <Paper className="brain-composer" elevation={0}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                fullWidth
                multiline
                maxRows={5}
                placeholder="Ask Ezee AI what's happening..."
                value={question}
                disabled={loading}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    ask();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={() => ask()}
                disabled={loading || !question.trim()}
                className="brain-ask-button"
              >
                Ask
              </Button>
            </Stack>
            <Typography variant="caption" color="textSecondary" mt={1} display="block">
              Ezee reads live Core data by default. Controlled write actions require the normal server-enforced confirmation flow.
            </Typography>
          </Paper>
        </>
      )}
    </Stack>
  );
};

export default BrainTab;
